import { Briefcase, Tag, Calendar, Globe, Target, Layers, Sparkles, FileText, Users, TrendingUp, AlertCircle } from 'lucide-react';

const CAMPAIGN_TYPE_LABELS = {
  social_campaign: 'Social Campaign',
  ad_commercial: 'Ad / Commercial',
  brand_partnership: 'Brand Partnership',
  product_launch: 'Product Launch',
  user_generated_content: 'User-Generated Content',
  ambassador_program: 'Ambassador Program',
};

const CAMPAIGN_TYPE_DESC = {
  social_campaign: 'Coordinated posts across social channels using your likeness',
  ad_commercial: 'Paid advertising — likely TV, web video, or display',
  brand_partnership: 'Ongoing affiliation as a brand spokesperson',
  product_launch: 'Featured creative for a specific product release',
  user_generated_content: 'Native, organic-style content for the brand\u2019s feeds',
  ambassador_program: 'Recurring activations across a longer time horizon',
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return null; }
}

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const days = Math.ceil((new Date(iso) - new Date()) / 86400000);
    if (days < 0) return null;
    if (days === 0) return 'starts today';
    if (days === 1) return 'starts tomorrow';
    if (days < 30) return `starts in ${days} days`;
    return `starts in ${Math.round(days / 30)} months`;
  } catch { return null; }
}

export default function CampaignBriefCard({ campaign }) {
  if (!campaign) return null;
  const typeKey = campaign.campaign_type;
  const typeLabel = CAMPAIGN_TYPE_LABELS[typeKey] || typeKey || 'Campaign';
  const typeDesc = CAMPAIGN_TYPE_DESC[typeKey] || '';
  const start = fmtDate(campaign.start_date);
  const end = fmtDate(campaign.end_date);
  const startsIn = daysUntil(campaign.start_date);
  const deliverables = campaign.deliverable_types || [];
  const channels = campaign.channels || [];
  const territoryDisplay = Array.isArray(campaign.territory)
    ? campaign.territory.join(', ').replace(/_/g, ' ')
    : (typeof campaign.territory === 'string' ? campaign.territory.replace(/_/g, ' ') : '');
  const description = (campaign.description || '').trim();
  const objective = (campaign.objective || '').trim();
  const targetAudience = (campaign.target_audience || '').trim();
  const brandTone = (campaign.brand_tone || campaign.tone || '').trim();
  const references = campaign.references || campaign.reference_links || [];

  const hasContent = description || objective || targetAudience || brandTone ||
                     deliverables.length || channels.length || start || end || territoryDisplay;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-5" data-testid="campaign-brief-card">
      {/* HERO — branded header */}
      <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 px-6 py-7 text-white overflow-hidden">
        {/* Decorative dots pattern */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }} />

        <div className="relative">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-100 font-semibold">
              <Briefcase className="w-3.5 h-3.5" /> Campaign brief
            </div>
            <span className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-[11px] font-medium border border-white/30 shrink-0">
              {typeLabel}
            </span>
          </div>

          <h3 className="text-2xl font-bold mb-1 leading-tight" data-testid="campaign-brief-name">{campaign.name}</h3>
          {campaign.brand && campaign.brand !== campaign.name && (
            <p className="text-blue-100 text-sm">
              <span className="opacity-75">For </span>
              <strong className="text-white font-semibold">{campaign.brand}</strong>
            </p>
          )}
          {typeDesc && (
            <p className="text-blue-100/90 text-xs mt-2 leading-relaxed max-w-2xl">{typeDesc}</p>
          )}

          {startsIn && (
            <div className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full bg-emerald-400/20 border border-emerald-200/40 text-[11px] font-medium">
              <Sparkles className="w-3 h-3" /> {startsIn}
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        {!hasContent && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 flex items-start gap-3" data-testid="campaign-brief-empty">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-amber-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-900">The agency hasn&apos;t filled out the campaign brief yet</p>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                Before accepting, you have the right to ask for more context — description, deliverables, timeline, channels, brand tone. Click <strong className="font-semibold">Counter</strong> below to request these details.
              </p>
            </div>
          </div>
        )}

        {/* Description — most important narrative */}
        {description && (
          <div className="mb-5" data-testid="campaign-description">
            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
              <FileText className="w-3 h-3" /> The brief
            </div>
            <p className="text-[14px] text-slate-700 leading-relaxed whitespace-pre-line">{description}</p>
          </div>
        )}

        {/* Objective + Target audience side-by-side */}
        {(objective || targetAudience) && (
          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            {objective && (
              <div className="rounded-xl bg-purple-50/60 border border-purple-100 p-4">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Target className="w-3.5 h-3.5 text-purple-600" />
                  <p className="text-[10px] font-bold text-purple-700 uppercase tracking-widest">Objective</p>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">{objective}</p>
              </div>
            )}
            {targetAudience && (
              <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Users className="w-3.5 h-3.5 text-emerald-600" />
                  <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Target audience</p>
                </div>
                <p className="text-xs text-slate-700 leading-relaxed">{targetAudience}</p>
              </div>
            )}
          </div>
        )}

        {/* Quick facts grid — 4-up */}
        {(start || end || territoryDisplay || brandTone) && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            {(start || end) && (
              <FactBox icon={Calendar} label="Timeline" value={start && end ? `${start} → ${end}` : start || end} accent="blue" />
            )}
            {territoryDisplay && (
              <FactBox icon={Globe} label="Territory" value={territoryDisplay} accent="teal" />
            )}
            {brandTone && (
              <FactBox icon={TrendingUp} label="Brand tone" value={brandTone} accent="rose" />
            )}
            <FactBox icon={Briefcase} label="Type" value={typeLabel} accent="amber" />
          </div>
        )}

        {/* Deliverables — important enough for its own block */}
        {!!deliverables.length && (
          <div className="mb-4">
            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
              <Layers className="w-3 h-3" /> Deliverables ({deliverables.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {deliverables.map(d => (
                <span key={d} className="text-xs px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 font-medium">
                  {d.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Channels */}
        {!!channels.length && (
          <div className="mb-2">
            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
              <Tag className="w-3 h-3" /> Channels ({channels.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {channels.map(c => (
                <span key={c} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 font-medium">
                  {c.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Reference links (if provided) */}
        {Array.isArray(references) && references.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">References & mood</p>
            <div className="flex flex-wrap gap-2">
              {references.slice(0, 6).map((r, i) => {
                const url = typeof r === 'string' ? r : r?.url;
                const label = typeof r === 'string' ? r : (r?.label || r?.url);
                if (!url) return null;
                return (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 hover:bg-blue-50 text-slate-600 hover:text-blue-700 transition-colors truncate max-w-[200px]">
                    {label}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function FactBox({ icon: Icon, label, value, accent }) {
  const colors = {
    blue:  { bg: 'bg-blue-50/60',  border: 'border-blue-100',  icon: 'text-blue-600',  label: 'text-blue-700' },
    teal:  { bg: 'bg-teal-50/60',  border: 'border-teal-100',  icon: 'text-teal-600',  label: 'text-teal-700' },
    rose:  { bg: 'bg-rose-50/60',  border: 'border-rose-100',  icon: 'text-rose-600',  label: 'text-rose-700' },
    amber: { bg: 'bg-amber-50/60', border: 'border-amber-100', icon: 'text-amber-600', label: 'text-amber-700' },
  };
  const c = colors[accent] || colors.blue;
  return (
    <div className={`rounded-xl ${c.bg} border ${c.border} p-3.5`}>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${c.icon}`} />
        <p className={`text-[10px] font-bold ${c.label} uppercase tracking-widest`}>{label}</p>
      </div>
      <p className="text-xs text-slate-800 font-medium capitalize leading-snug">{value}</p>
    </div>
  );
}
